"use client"

import { createContext, useContext, useEffect, useState, useRef } from "react"
import { User } from "@supabase/supabase-js"
import { createClient } from "@/lib/supabase/client"
import { usePlatform } from "@/hooks/use-platform"
import { useRouter } from "next/navigation"

interface AuthContextType {
  user: User | null
  loading: boolean
  signOut: () => Promise<void>
  signInWithGoogle: () => Promise<void>
  signInWithApple: () => Promise<void>
}

const AuthContext = createContext<AuthContextType | undefined>(undefined)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true) // Start with true to prevent premature redirects
  // Track last applied user id to avoid redundant state churn on repeated auth events
  const lastUserIdRef = useRef<string | null>(null)
  
  const { isNative } = usePlatform()
  
  const router = useRouter()
  
  // Reduced logging to prevent console spam
  // console.log('AuthProvider rendering - initial state:', { user, loading })
  
  // Use the singleton client to avoid multiple instances
  const supabase = createClient()

  // Handle deep links for OAuth redirects in native app
  useEffect(() => {
    if (typeof window === 'undefined') return

    const handleDeepLink = async (url: string) => {
      console.log('Auth context - handling deep link:', url)
      
      // Parse the URL
      const urlObj = new URL(url.replace('com.spotstosurf.app://', 'https://placeholder.com/'))
      
      // Check for PKCE flow (code in search params)
      const code = urlObj.searchParams.get('code')
      
      if (code) {
        console.log('Auth context - found code in deep link, exchanging for session')
        
        // Close the in-app browser if it's open
        try {
          const { Browser } = await import('@capacitor/browser')
          await Browser.close()
        } catch (e) {
          console.log('Auth context - could not close browser (might not be open or plugin missing)')
        }

        const { error } = await supabase.auth.exchangeCodeForSession(code)
        
        if (error) {
          console.error('Auth context - error exchanging code from deep link:', error.message)
        } else {
          console.log('Auth context - session exchanged successfully from deep link')
          if (typeof window !== 'undefined') {
            setTimeout(() => {
              router.replace('/home')
            }, 100)
          }
        }
        return
      }

      // Fallback for older implicit flow (hash)
      const hash = urlObj.hash.substring(1) // Remove the #
      if (hash) {
        const params = new URLSearchParams(hash)
        const access_token = params.get('access_token')
        const refresh_token = params.get('refresh_token')
        
        if (access_token && refresh_token) {
          console.log('Auth context - found tokens in deep link, manually setting session')
          
          // Close the in-app browser if it's open
          try {
            const { Browser } = await import('@capacitor/browser')
            await Browser.close()
          } catch (e) {
            console.log('Auth context - could not close browser (might not be open or plugin missing)')
          }

          const { error } = await supabase.auth.setSession({
            access_token,
            refresh_token
          })
          
          if (error) {
            console.error('Auth context - error setting session from deep link:', error.message)
          } else {
            console.log('Auth context - session set successfully from deep link')
            if (typeof window !== 'undefined') {
              setTimeout(() => {
                router.replace('/home')
              }, 100)
            }
          }
        }
      }
    }

    // Try to import App plugin dynamically to avoid SSR/web issues
    let appPlugin: any = null
    const setupAppListener = async () => {
      try {
        const { App } = await import('@capacitor/app')
        appPlugin = App
        
        App.addListener('appUrlOpen', (event: any) => {
          handleDeepLink(event.url)
        })
        
        // Handle app started from a deep link
        const launchUrl = await App.getLaunchUrl()
        if (launchUrl?.url) {
          handleDeepLink(launchUrl.url)
        }
      } catch (e) {
        // Plugin might not be installed or not on native
        console.log('Auth context - Capacitor App plugin not available')
      }
    }

    setupAppListener()

    return () => {
      // Cleanup listener if possible
    }
  }, [supabase, router])

  useEffect(() => {
    let isMounted = true
    let timeoutId: ReturnType<typeof setTimeout> | null = null
    
    // Initialize: Get session from Supabase client
    // The client automatically reads from localStorage and validates with server
    const initializeAuth = async () => {
      // Only run on client side to prevent hydration mismatches
      if (typeof window === 'undefined') {
        setLoading(false);
        return;
      }
      
      try {
        // First, check localStorage directly to see if session exists
          const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
          const match = supabaseUrl.match(/https?:\/\/([^.]+)\.supabase\.co/)
          const projectRef = match ? match[1] : 'default'
          const storageKey = `sb-${projectRef}-auth-token`
          const stored = localStorage.getItem(storageKey)
          
          if (stored) {
        try {
              const parsed = JSON.parse(stored)
              console.log('Auth context - localStorage found:', {
                hasCurrentSession: !!parsed?.currentSession,
                hasAccessToken: !!parsed?.currentSession?.access_token,
                hasUser: !!parsed?.currentSession?.user,
                userEmail: parsed?.currentSession?.user?.email,
                // Check alternative structure
                hasAccessTokenAlt: !!parsed?.access_token,
                hasUserAlt: !!parsed?.user,
                keys: Object.keys(parsed || {}),
          })
          
              // Check if we just signed out - if so, don't restore
              const signoutFlag = typeof window !== 'undefined' ? sessionStorage.getItem('auth_signout_flag') : null
              if (signoutFlag === 'true') {
                console.log('Auth context - signout flag detected, skipping manual restore')
                // Clear the flag after checking
                if (typeof window !== 'undefined') {
                  sessionStorage.removeItem('auth_signout_flag')
                }
              } else {
                  // Try to restore session manually if structure is different
                  // Check both currentSession and root level
                  // Only restore if we don't already have a user (prevents restore after sign out)
                  const session = parsed?.currentSession || parsed
                  if (session?.access_token && session?.user && !user && lastUserIdRef.current !== session.user.id) {
                    // Check if session is not expired (basic check - access_token exists)
                    // Also verify the session structure looks valid
                    if (session.user?.id && session.user?.email) {
                      console.log('Auth context - manually restoring session from localStorage:', session.user.email)
                      setUser(session.user as User)
                      lastUserIdRef.current = session.user.id
                      
                      // Sync to cookies
                      fetch('/api/auth/session', {
                        method: 'GET',
                        credentials: 'include',
                        headers: {
                          'Authorization': `Bearer ${session.access_token}`,
                        },
                      }).then(res => {
                        console.log('Auth context - manual cookie sync response:', res.status)
                      }).catch((err) => {
                        console.log('Auth context - manual cookie sync error:', err)
                      })
                    } else {
                      console.log('Auth context - session in localStorage appears invalid, skipping restore')
                    }
                  }
                }
            } catch (e) {
              console.log('Auth context - localStorage parse error:', e)
            }
          } else {
            console.log('Auth context - no session found')
            // List all localStorage keys to debug
            const allKeys = Object.keys(localStorage).filter(k => k.includes('supabase') || k.includes('sb-'))
            console.log('Auth context - all Supabase-related keys:', allKeys)
            if (allKeys.length > 0) {
              // Show what's in the first key
              try {
                const firstKey = allKeys[0]
                const firstValue = localStorage.getItem(firstKey)
                console.log('Auth context - first key content:', { key: firstKey, value: firstValue?.substring(0, 200) })
              } catch (e) {
                console.log('Auth context - error reading first key:', e)
              }
            }
          }
        
        // Use getSession() which reads from localStorage and validates
        // Make it non-blocking with a short timeout - onAuthStateChange will handle updates
        const sessionPromise = supabase.auth.getSession()
        const timeoutPromise = new Promise<{ data: { session: null }, error: null }>((resolve) => {
          timeoutId = setTimeout(() => {
            console.log('Auth context - getSession timeout, relying on onAuthStateChange')
            resolve({ data: { session: null }, error: null })
          }, 2000) // Short timeout - don't block on this
        })
        
        // Don't await - let it run in background, onAuthStateChange will handle updates
        Promise.race([sessionPromise, timeoutPromise]).then(async (result) => {
          if (!isMounted) return
          const { data: { session }, error } = result as { data: { session: any }, error: any }
        
        if (error) {
          console.log('Auth context - getSession error:', error.message)
            // If we have a user but getSession failed, try to refresh
            if (lastUserIdRef.current) {
              console.log('Auth context - User exists but getSession failed, attempting refresh...')
            try {
              const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession()
              if (refreshError) {
                console.log('Auth context - Refresh failed:', refreshError.message)
                // Clear expired tokens from localStorage when refresh fails
                if (typeof window !== 'undefined') {
                  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
                  const match = supabaseUrl.match(/https?:\/\/([^.]+)\.supabase\.co/)
                  const projectRef = match ? match[1] : 'default'
                  const storageKey = `sb-${projectRef}-auth-token`
                  localStorage.removeItem(storageKey)
                  
                  // Clear all Supabase-related keys
                  const keys = Object.keys(localStorage)
                  keys.forEach(key => {
                    if (key.startsWith('sb-') || key.includes('supabase')) {
                      localStorage.removeItem(key)
                    }
                  })
                  console.log('Auth context - Cleared expired tokens from localStorage')
                }
                setUser(null)
                lastUserIdRef.current = null
              } else if (refreshedSession?.user) {
                  console.log('Auth context - Session refreshed successfully:', refreshedSession.user.email)
                  setUser(refreshedSession.user)
                  lastUserIdRef.current = refreshedSession.user.id
                  
                  // Sync refreshed session to cookies
                  if (typeof window !== 'undefined' && refreshedSession.access_token) {
                    fetch('/api/auth/session', {
                      method: 'GET',
                      credentials: 'include',
                      headers: {
                        'Authorization': `Bearer ${refreshedSession.access_token}`,
                      },
                    }).then(res => {
                      console.log('Auth context - refreshed cookie sync response:', res.status)
                    }).catch((err) => {
                      console.log('Auth context - refreshed cookie sync error:', err)
                    })
                  }
                }
              } catch (refreshErr) {
                console.log('Auth context - Refresh exception:', refreshErr)
                setUser(null)
                lastUserIdRef.current = null
              }
            }
        } else if (session?.user) {
            console.log('Auth context - found session:', session.user.email)
          if (lastUserIdRef.current !== session.user.id) {
            setUser(session.user)
            lastUserIdRef.current = session.user.id
              
              // Sync session to server cookies by making a request that triggers middleware
              if (typeof window !== 'undefined' && session.access_token) {
                fetch('/api/auth/session', {
                  method: 'GET',
                  credentials: 'include',
                  headers: {
                    'Authorization': `Bearer ${session.access_token}`,
                  },
                }).then(res => {
                  console.log('Auth context - cookie sync response:', res.status)
                }).catch((err) => {
                  console.log('Auth context - cookie sync error:', err)
                })
              }
            }
          } else if (!session && lastUserIdRef.current) {
            // No session but we have a user - token might be expired, try to refresh
            console.log('Auth context - No session but user exists, attempting refresh...')
            try {
              const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession()
              if (refreshError) {
                console.log('Auth context - Refresh failed:', refreshError.message)
                // Clear expired tokens from localStorage when refresh fails
                if (typeof window !== 'undefined') {
                  const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
                  const match = supabaseUrl.match(/https?:\/\/([^.]+)\.supabase\.co/)
                  const projectRef = match ? match[1] : 'default'
                  const storageKey = `sb-${projectRef}-auth-token`
                  localStorage.removeItem(storageKey)
                  
                  // Clear all Supabase-related keys
                  const keys = Object.keys(localStorage)
                  keys.forEach(key => {
                    if (key.startsWith('sb-') || key.includes('supabase')) {
                      localStorage.removeItem(key)
                    }
                  })
                  console.log('Auth context - Cleared expired tokens from localStorage')
                }
                setUser(null)
                lastUserIdRef.current = null
              } else if (refreshedSession?.user) {
                console.log('Auth context - Session refreshed successfully:', refreshedSession.user.email)
                setUser(refreshedSession.user)
                lastUserIdRef.current = refreshedSession.user.id
                
                // Sync refreshed session to cookies
                if (typeof window !== 'undefined' && refreshedSession.access_token) {
                  fetch('/api/auth/session', {
                    method: 'GET',
                    credentials: 'include',
                    headers: {
                      'Authorization': `Bearer ${refreshedSession.access_token}`,
                    },
                  }).then(res => {
                    console.log('Auth context - refreshed cookie sync response:', res.status)
                  }).catch((err) => {
                    console.log('Auth context - refreshed cookie sync error:', err)
                  })
                }
              }
            } catch (refreshErr) {
              console.log('Auth context - Refresh exception:', refreshErr)
              // Clear expired tokens from localStorage when refresh fails
              if (typeof window !== 'undefined') {
                const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
                const match = supabaseUrl.match(/https?:\/\/([^.]+)\.supabase\.co/)
                const projectRef = match ? match[1] : 'default'
                const storageKey = `sb-${projectRef}-auth-token`
                localStorage.removeItem(storageKey)
                
                // Clear all Supabase-related keys
                const keys = Object.keys(localStorage)
                keys.forEach(key => {
                  if (key.startsWith('sb-') || key.includes('supabase')) {
                    localStorage.removeItem(key)
                  }
                })
                console.log('Auth context - Cleared expired tokens from localStorage (onAuthStateChange)')
              }
              setUser(null)
              lastUserIdRef.current = null
            }
          }
        }).catch(() => {
          // Ignore - onAuthStateChange will handle it
        })
        
        // Set loading to false after a brief delay to allow manual restore or onAuthStateChange
        // This gives time for the session to be restored before redirect logic runs
        setTimeout(() => {
          if (isMounted) {
            setLoading(false)
          }
        }, 100) // Small delay to allow restore
      } catch (error) {
        if (isMounted) {
          console.log('Auth context - getSession exception:', error)
          setLoading(false)
        }
      }
    }

    initializeAuth()

    // Listen for auth changes (login, logout, token refresh, etc.)
    // This ensures the UI updates immediately when auth state changes
    // This is the PRIMARY way to restore sessions - it fires on mount with INITIAL_SESSION
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('Auth state changed:', event, session?.user?.email || 'no user')
        
        if (!isMounted) return
        
        // If we have a user in state but no session (token expired), try to refresh
        // Check lastUserIdRef to see if we had a user before
        if (lastUserIdRef.current && !session && event !== 'SIGNED_OUT') {
          console.log('Auth context - User exists but no session, attempting to refresh...')
          try {
            const { data: { session: refreshedSession }, error: refreshError } = await supabase.auth.refreshSession()
            if (refreshError) {
              console.log('Auth context - Refresh failed:', refreshError.message)
              // Clear expired tokens from localStorage when refresh fails
              if (typeof window !== 'undefined') {
                const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
                const match = supabaseUrl.match(/https?:\/\/([^.]+)\.supabase\.co/)
                const projectRef = match ? match[1] : 'default'
                const storageKey = `sb-${projectRef}-auth-token`
                localStorage.removeItem(storageKey)
                
                // Clear all Supabase-related keys
                const keys = Object.keys(localStorage)
                keys.forEach(key => {
                  if (key.startsWith('sb-') || key.includes('supabase')) {
                    localStorage.removeItem(key)
                  }
                })
                console.log('Auth context - Cleared expired tokens from localStorage (onAuthStateChange refresh)')
              }
              // If refresh fails, user is truly logged out
              setUser(null)
              lastUserIdRef.current = null
            } else if (refreshedSession?.user) {
              console.log('Auth context - Session refreshed successfully:', refreshedSession.user.email)
              setUser(refreshedSession.user)
              lastUserIdRef.current = refreshedSession.user.id
              
              // Sync refreshed session to cookies
              if (typeof window !== 'undefined' && refreshedSession.access_token) {
                fetch('/api/auth/session', {
                  method: 'GET',
                  credentials: 'include',
                  headers: {
                    'Authorization': `Bearer ${refreshedSession.access_token}`,
                  },
                }).then(res => {
                  console.log('Auth context - refreshed cookie sync response:', res.status)
                }).catch((err) => {
                  console.log('Auth context - refreshed cookie sync error:', err)
                })
              }
            }
          } catch (refreshErr) {
            console.log('Auth context - Refresh exception:', refreshErr)
            // If refresh fails, user is truly logged out
            setUser(null)
            lastUserIdRef.current = null
          }
          setLoading(false)
          return
        }
        
        // Update user state when it changes
        const nextUserId = session?.user?.id ?? null
        if (lastUserIdRef.current !== nextUserId) {
          if (session?.user) {
            console.log('Auth context - updating user:', session.user.email, 'event:', event)
            setUser(session.user)
          lastUserIdRef.current = nextUserId
            
            // Sync session to server cookies for INITIAL_SESSION, SIGNED_IN, or TOKEN_REFRESHED
            // This ensures cookies are set when session is restored from localStorage
            if ((event === 'INITIAL_SESSION' || event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') 
                && typeof window !== 'undefined' && session.access_token) {
              fetch('/api/auth/session', {
                method: 'GET',
              credentials: 'include',
                headers: {
                  'Authorization': `Bearer ${session.access_token}`,
                },
              }).then(res => {
                console.log('Auth context - cookie sync response:', res.status)
              }).catch((err) => {
                console.log('Auth context - cookie sync error:', err)
              })
            }
          } else {
            console.log('Auth context - clearing user, event:', event)
            setUser(null)
            lastUserIdRef.current = null
            
            // If SIGNED_OUT event, also clear localStorage to prevent restore on refresh
            if (event === 'SIGNED_OUT') {
              if (typeof window !== 'undefined') {
                console.log('Auth context - SIGNED_OUT event, clearing localStorage')
                const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
                const match = supabaseUrl.match(/https?:\/\/([^.]+)\.supabase\.co/)
                const projectRef = match ? match[1] : 'default'
                const storageKey = `sb-${projectRef}-auth-token`
                localStorage.removeItem(storageKey)
                
                // Clear all Supabase-related keys
                const keys = Object.keys(localStorage)
                keys.forEach(key => {
                  if (key.startsWith('sb-') || key.includes('supabase')) {
                    localStorage.removeItem(key)
                  }
                })
                console.log('Auth context - localStorage cleared on SIGNED_OUT')
          }
        }
          }
        }
        setLoading(false)
      }
    )

    return () => {
      isMounted = false
      if (timeoutId) clearTimeout(timeoutId)
      subscription.unsubscribe()
    }
  }, [])

  const signOut = async () => {
    try {
      console.log("Auth context - signing out...")
      
      // Clear user state first to provide immediate feedback
      setUser(null)
      lastUserIdRef.current = null
      
      // Clear localStorage FIRST before calling signOut
      // This prevents the session from being restored on refresh
      if (typeof window !== 'undefined') {
        console.log("Auth context - clearing localStorage")
        const supabaseUrl = process.env.NEXT_PUBLIC_SUPABASE_URL || ''
        const match = supabaseUrl.match(/https?:\/\/([^.]+)\.supabase\.co/)
        const projectRef = match ? match[1] : 'default'
        const storageKey = `sb-${projectRef}-auth-token`
        
        // Set a flag in sessionStorage to prevent restore on next mount
        sessionStorage.setItem('auth_signout_flag', 'true')
        
        // Remove the specific Supabase auth token
        localStorage.removeItem(storageKey)
        
        // Also remove any other Supabase-related keys
        const keys = Object.keys(localStorage)
        keys.forEach(key => {
          if (key.startsWith('sb-') || key.includes('supabase')) {
            localStorage.removeItem(key)
          }
        })
        
        console.log("Auth context - localStorage cleared")
      }
      
      // Then call Supabase signOut to clear server session
      try {
        const { error } = await supabase.auth.signOut()
        if (error) {
          console.error("Auth context - sign out error:", error)
        } else {
          console.log("Auth context - sign out successful")
        }
      } catch (signOutError) {
        console.error("Auth context - signOut method failed:", signOutError)
      }
      
    } catch (error) {
      console.error("Auth context - sign out failed:", error)
      // User state is already cleared, so we don't need to do anything else
    }
  }

  const signInWithGoogle = async () => {
    try {
      console.log("Auth context - signing in with Google...")
      
      // Determine the correct redirect URL based on environment
      // ALWAYS check the window object directly at the moment of click
      const win = typeof window !== 'undefined' ? window as any : null
      const capPlatform = win?.Capacitor?.platform
      const capIsNative = win?.Capacitor?.isNative
      const userAgent = win?.navigator?.userAgent || ''
      const isStandalone = win?.matchMedia?.('(display-mode: standalone)')?.matches || win?.navigator?.standalone === true
      
      // WKWebView (iOS app) has 'Mobile' but not 'Safari'
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) || (win?.navigator?.platform === 'MacIntel' && win?.navigator?.maxTouchPoints > 1)
      const isWKWebView = isIOS && /Mobile/.test(userAgent) && !/Safari/.test(userAgent)
      
      const isNativeApp = !!(capIsNative) || 
                         (capPlatform === 'ios' || capPlatform === 'android') || 
                         !!(userAgent.includes('Capacitor')) ||
                         !!(userAgent.includes('SpotsToSurfApp')) ||
                         !!(win?.location?.href?.includes('capacitor://')) ||
                         (isWKWebView && !isStandalone) ||
                         isNative // Fallback to context
      
      const redirectTo = isNativeApp 
        ? `com.spotstosurf.app://auth/callback` 
        : `${window.location.origin}/auth/callback`

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo,
          skipBrowserRedirect: isNativeApp, // Important: don't auto-redirect on native, return URL instead
          queryParams: {
            prompt: 'select_account' // Forces the Google account chooser to appear instead of auto-login
          }
        }
      })

      if (error) throw error

      // On native, manually open the URL in the in-app browser
      if (isNativeApp && data?.url) {
        console.log('Auth context - opening in-app browser for native Google login')
        try {
          const { Browser } = await import('@capacitor/browser')
          await Browser.open({ url: data.url, windowName: '_self' })
        } catch (e) {
          console.error('Auth context - failed to open in-app browser:', e)
          // Fallback to window.location if browser plugin fails
          window.location.href = data.url
        }
      }
    } catch (error) {
      console.error("Auth context - Google sign in failed:", error)
      throw error
    }
  }

  const signInWithApple = async () => {
    try {
      console.log("Auth context - signing in with Apple...")
      
      // ALWAYS check the window object directly at the moment of click
      const win = typeof window !== 'undefined' ? window as any : null
      const capPlatform = win?.Capacitor?.platform
      const capIsNative = win?.Capacitor?.isNative
      const userAgent = win?.navigator?.userAgent || ''
      const isStandalone = win?.matchMedia?.('(display-mode: standalone)')?.matches || win?.navigator?.standalone === true
      
      // WKWebView (iOS app) has 'Mobile' but not 'Safari'
      const isIOS = /iPad|iPhone|iPod/.test(userAgent) || (win?.navigator?.platform === 'MacIntel' && win?.navigator?.maxTouchPoints > 1)
      const isWKWebView = isIOS && /Mobile/.test(userAgent) && !/Safari/.test(userAgent)
      
      const isNativeApp = !!(capIsNative) || 
                         (capPlatform === 'ios' || capPlatform === 'android') || 
                         !!(userAgent.includes('Capacitor')) ||
                         !!(userAgent.includes('SpotsToSurfApp')) ||
                         !!(win?.location?.href?.includes('capacitor://')) ||
                         (isWKWebView && !isStandalone) ||
                         isNative // Fallback to context
      
      const redirectTo = isNativeApp 
        ? `com.spotstosurf.app://auth/callback` 
        : `${window.location.origin}/auth/callback`

      const { data, error } = await supabase.auth.signInWithOAuth({
        provider: 'apple',
        options: {
          redirectTo,
          skipBrowserRedirect: isNativeApp
        }
      })

      if (error) throw error

      // On native, manually open the URL in the in-app browser
      if (isNativeApp && data?.url) {
        console.log('Auth context - opening in-app browser for native Apple login')
        try {
          const { Browser } = await import('@capacitor/browser')
          await Browser.open({ url: data.url, windowName: '_self' })
        } catch (e) {
          console.error('Auth context - failed to open in-app browser:', e)
          // Fallback to window.location if browser plugin fails
          window.location.href = data.url
        }
      }
    } catch (error) {
      console.error("Auth context - Apple sign in failed:", error)
      throw error
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, signOut, signInWithGoogle, signInWithApple }}>
      {children}
    </AuthContext.Provider>
  )
}

export const useAuth = () => {
  const context = useContext(AuthContext)
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider")
  }
  return context
}